import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import * as _ from 'lodash';
import { LoadingDialogComponent } from '../components/dialog/loading-dialog/loading-dialog.component';
import { ProcessingDialogComponent } from '../components/dialog/processing-dialog/processing-dialog.component';
import { FormWizardTemplate } from '../models/form-wizard-template.model';
import { LoginResult } from '../models/settings/login-result.model';
import { UserSessionDetails } from '../models/settings/user-session-details.model';
import { ErrorDialogComponent } from '../components/dialog/error-dialog/error-dialog.component';
import { AppConfigService } from './app-config.service';
import { Common } from '../common';
import { ConfigurationService } from './configuration.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private sessionDetailsKey = 'sessionDetails';
  private sessionDetails: UserSessionDetails;
  private loadingDialogRef?: DialogRef;
  private processingDialogRef?: DialogRef;

  constructor(
    private dialogService: DialogService,
    private appConfigService: AppConfigService,
    private configurationService: ConfigurationService,
    private http: HttpClient,
    private router: Router) {
    const sessionDetails = sessionStorage.getItem(this.getSessionDetailsKey());

    if (!_.isNil(sessionDetails) && sessionDetails !== '') {
      this.sessionDetails = JSON.parse(sessionDetails);
    } else {
      this.sessionDetails = new UserSessionDetails();
      this.sessionDetails.configuration = this.appConfigService.getConfig().defaultConfiguration;
    }

    this.setConfiguration();
  }

  private setUserSessionStorage(): void {
    sessionStorage.setItem(this.getSessionDetailsKey(), JSON.stringify(this.sessionDetails));
  }

  private setConfiguration(): void {
    // If there is no supplied configuration, use the default settings
    this.configurationService.setConfiguration(this.sessionDetails.configuration ?? this.appConfigService.getConfig().defaultConfiguration);
  }

  public logout(): void {
    // Send logout request to server, we don't need to wait for this however it is done before clearing the session details so we still have the bearerToken
    this.http.post<LoginResult>(`${this.appConfigService.getConfig().apiUrl}authentication/logout`, { }).subscribe();

    this.sessionDetails.bearerToken = '';
    this.sessionDetails.authenticationStatus = 'UNAUTHORIZED';
    this.sessionDetails.configuration = this.appConfigService.getConfig().defaultConfiguration;

    this.setUserSessionStorage();
    this.setConfiguration();

    this.router.navigate(['/login']);
  }

  public isAuthenticated(): boolean {
    return this.sessionDetails.authenticationStatus !== 'UNAUTHORIZED';
  }

  public setSessionDetailsFromLoginResult(result: LoginResult) {
    this.sessionDetails.bearerToken = result.accessToken;
    this.sessionDetails.authenticationStatus = result.authStatus;
    this.sessionDetails.userId = result.userId;
    this.sessionDetails.configuration = result.debtorPortalConfiguration;

    this.setUserSessionStorage();
    this.setConfiguration();
  }

  public getBearerToken(): string {
    return this.sessionDetails.bearerToken;
  }

  public getUserId(): string {
    return this.sessionDetails.userId;
  }

  public getContactUsFormWizard(contactInfo: string): FormWizardTemplate {
    return {
      label: 'How can we help?',
      description: this.configurationService.getHowCanWeHelpText(),
      rightSectionHtml: contactInfo,
      pages: [
        {
          label: '',
          previousButtonLabel: '',
          nextButtonLabel: '',
          completeButtonLabel: 'Submit',
          completeButtonClasses: 'button-primary',
          elements: [
            {
              type: 'textInput',
              name: 'name',
              label: 'Name',
              class: 'col-12',
              required: true,
              defaultValue: ''
            },
            {
              type: 'textInput',
              name: 'referenceId',
              label: 'Reference Number',
              class: 'col-12',
              required: true,
              defaultValue: ''
            },
            {
              type: 'textInput',
              name: 'phoneNumber',
              label: 'Contact Number',
              class: 'col-12',
              required: false,
              defaultValue: ''
            },
            {
              type: 'textInput',
              name: 'emailAddr',
              label: 'Email',
              class: 'col-12',
              required: true,
              defaultValue: '',
              pattern: Common.getValidationEmailAddressRegex()
            },
            {
              type: 'textArea',
              name: 'message',
              label: 'Your message',
              class: 'col-12',
              required: true,
              defaultValue: ''
            }
          ]
        }
      ]
    } as FormWizardTemplate;
  }

  public getContactSuccessMessage(): string {
    return 'Message has been successfully sent.';
  }

  public showLoading(): void {
    if (!_.isNil(this.loadingDialogRef)) {
      this.loadingDialogRef.close();
      this.loadingDialogRef = undefined;
    }

    this.loadingDialogRef = this.dialogService.open({
      content: LoadingDialogComponent,
      preventAction: (ev) => {
        return true;
      }
    });
  }

  public hideLoading(): void {
    if (!_.isNil(this.loadingDialogRef)) {
      this.loadingDialogRef.close();
      this.loadingDialogRef = undefined;
    }
  }

  public showProcessing(): void {
    if (!_.isNil(this.processingDialogRef)) {
      this.processingDialogRef.close();
      this.processingDialogRef = undefined;
    }

    this.processingDialogRef = this.dialogService.open({
      content: ProcessingDialogComponent,
      preventAction: (ev) => {
        return true;
      }
    });
  }

  public hideProcessing(): void {
    if (!_.isNil(this.processingDialogRef)) {
      this.processingDialogRef.close();
      this.processingDialogRef = undefined;
    }
  }

  public isProcessing(): boolean {
    return !_.isNil(this.processingDialogRef);
  }

  public showErrorDialog(errorMessage: string): void {
    const errorDialog = this.dialogService.open({
      content: ErrorDialogComponent,
      autoFocusedElement: '#errorMessage'
    });

    errorDialog.content.instance.errorMessage = errorMessage;
  }

  private getSessionDetailsKey(): string {
    return `${this.sessionDetailsKey}-${this.appConfigService.getConfig().htmlVersion}`;
  }
}
